/**
 * ErrorBanner — shared alert primitive.
 *
 * Replaces the identical inline `<div role="alert" ...>` blocks in
 * SearchPage, SchoolDetailPage, and WatchlistPage.
 *
 * Design system tokens:
 *   - statusErrorBg (bg-red-50), statusErrorBorder (border-red-200),
 *     statusErrorText (text-red-700)
 *
 * No Radix dependency — `role="alert"` on a plain <div> is the correct
 * ARIA pattern for a non-dismissible live region. Dismissible variant uses
 * the optional `onDismiss` prop.
 */

import React from 'react';

interface Props {
  message: string;
  onDismiss?: () => void;
}

export function ErrorBanner({ message, onDismiss }: Props) {
  return (
    <div
      role="alert"
      aria-live="polite"
      className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-start justify-between gap-3"
    >
      <span>{message}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="underline hover:no-underline flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2 rounded"
          aria-label="Dismiss error"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}
